import * as React from 'react'
import styled from 'styled-components'
import { colors } from '@components/shared/colors'
import { FlameIcon } from './FlameIcon'
import type { TrendingReviewType } from '../../services/userReviews'

export type ReviewCardPropsType = {
  review: TrendingReviewType,
  compact?: boolean,
  onContentPress?: (contentId: string) => void
}

export function ReviewCard ({ review, compact = false, onContentPress }: ReviewCardPropsType): React.ReactNode {
  const showFavorites = review.ratings?.overall && (review.ratings?.overall || 0) > 2
  const content = review.content

  return (
    <CardContainer $compact={compact}>
      <ContentWrapper $compact={compact}>
        {review.reviewText && (
          <ReviewText $compact={compact}>
            {review.reviewText}
          </ReviewText>
        )}

        <Footer>
          <UserInfo>
            <UserEmoji $color={review.user.color}>
              {review.user.emoji}
            </UserEmoji>
            <Username $compact={compact}>
              {review.user.username}
            </Username>
          </UserInfo>

          {showFavorites && (
            <FavoriteSection>
              <FavoriteCount>{review.favorites}</FavoriteCount>
              <FlameIcon filled={false} size={18} />
            </FavoriteSection>
          )}
        </Footer>
      </ContentWrapper>

      {content && content.title && content.imageUrl && (
        <ContentBar
          $compact={compact}
          $clickable={!!onContentPress}
          onClick={onContentPress ? (): void => onContentPress(review.contentId) : undefined}
        >
          <ContentImage src={content.imageUrl} $compact={compact} />
          <ContentTitle $compact={compact}>{content.title}</ContentTitle>
          <PlayIconWrapper>
            <PlayFilledImg src="/images/play-filled-icon.png" $compact={compact} />
          </PlayIconWrapper>
        </ContentBar>
      )}
    </CardContainer>
  )
}

const CardContainer = styled.div<any>`
  background-color: ${colors.midnightBlueTint};
  border-radius: 12px;
  overflow: hidden;
  ${(props: any): string => props.$compact && `
    margin: 4px 0;
  `}
`

const ContentWrapper = styled.div<any>`
  padding: ${(props: any): string => props.$compact ? '12px' : '20px'};
  display: flex;
  flex-direction: column;
  min-height: ${(props: any): string => props.$compact ? 'auto' : '140px'};
`

const ReviewText = styled.p<any>`
  font-family: "Good Sans Regular";
  font-size: ${(props: any): string => props.$compact ? '13px' : '14px'};
  line-height: ${(props: any): string => props.$compact ? '18px' : '20px'};
  color: ${colors.white};
  margin: 0 0 16px 0;
  flex: 1;

  ${(props: any): string => props.$compact && `
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-bottom: 12px;
  `}
`

const Footer = styled.div<any>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
`

const UserInfo = styled.div<any>`
  display: flex;
  align-items: center;
  flex: 1;
`

const UserEmoji = styled.div<any>`
  width: 26px;
  height: 26px;
  border-radius: 13px;
  background-color: ${(props: any): string => props.$color || colors.magenta};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 8px;
  font-size: 12px;
`

const Username = styled.span<any>`
  font-family: "Good Sans Bold";
  font-size: 12px;
  color: ${colors.white};
`

const FavoriteSection = styled.div<any>`
  display: flex;
  align-items: center;
  gap: 4px;
`

const FavoriteCount = styled.span<any>`
  font-family: "Good Sans Regular";
  font-size: 16px;
  color: ${colors.white};
`

const ContentBar = styled.div<any>`
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.15);
  padding: ${(props: any): string => props.$compact ? '8px' : '8px 12px'};
  cursor: ${(props: any): string => props.$clickable ? 'pointer' : 'default'};
`

const ContentImage = styled.img<any>`
  width: ${(props: any): string => props.$compact ? '32px' : '45px'};
  height: ${(props: any): string => props.$compact ? '32px' : '45px'};
  border-radius: 4px;
  margin-right: ${(props: any): string => props.$compact ? '8px' : '12px'};
  object-fit: cover;
`

const ContentTitle = styled.span<any>`
  flex: 1;
  font-family: "Good Sans Regular";
  font-size: 12px;
  color: ${colors.white};
  margin-right: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const PlayIconWrapper = styled.div<any>`
  margin-left: auto;
  flex-shrink: 0;
`

const PlayFilledImg = styled.img<any>`
  width: ${(props: any): string => props.$compact ? '24px' : '27px'};
  height: ${(props: any): string => props.$compact ? '24px' : '27px'};
`
